'use client';
import React, { useState } from 'react';
import TutorBuilderStep1 from './TutorBuilderStep1';
import TutorBuilderStep2 from './TutorBuilderStep2';
import TutorBuilderStep3 from './TutorBuilderStep3';
import TutorBuilderStep4 from './TutorBuilderStep4';
import TutorBuilderPreview from './TutorBuilderPreview';
import { CheckCircle2, Bot, BookOpen, Brain, Shield } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


export interface TutorConfig {
  name: string;
  personality: string;
  tone: string;
  avatar: string;
  courses: string[];
  difficulty: string;
  goals: string[];
  explanationStyle: string;
  summaryFrequency: string;
  memoryEnabled: boolean;
  ageRestriction: string;
  contentBoundaries: string[];
  parentApprovalRequired: boolean;
  safeMode: boolean;
}

const defaultConfig: TutorConfig = {
  name: '',
  personality: 'encouraging',
  tone: 'friendly',
  avatar: '🤖',
  courses: [],
  difficulty: 'intermediate',
  goals: [],
  explanationStyle: 'analogy',
  summaryFrequency: 'end-of-lesson',
  memoryEnabled: true,
  ageRestriction: '10-13',
  contentBoundaries: ['no-violence', 'no-adult-content'],
  parentApprovalRequired: true,
  safeMode: true,
};

const steps = [
  { id: 1, label: 'Tutor Identity', icon: Bot, desc: 'Name, personality & teaching tone' },
  { id: 2, label: 'Curriculum', icon: BookOpen, desc: 'Courses, difficulty & goals' },
  { id: 3, label: 'Behavior', icon: Brain, desc: 'How your tutor teaches & remembers' },
  { id: 4, label: 'Safety & Save', icon: Shield, desc: 'Age rules, boundaries & finalize' },
];

export default function AITutorBuilderContent() {
  const [currentStep, setCurrentStep] = useState(1);
  const [config, setConfig] = useState<TutorConfig>(defaultConfig);
  const [showPreview, setShowPreview] = useState(false);
  const [saved, setSaved] = useState(false);

  const updateConfig = (updates: Partial<TutorConfig>) => {
    setConfig((p) => ({ ...p, ...updates }));
  };

  const handleSave = async () => {
    // BACKEND INTEGRATION: POST /api/tutors with config, studentId
    await new Promise((r) => setTimeout(r, 1000));
    setSaved(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-foreground">AI Tutor Builder</h1>
        <p className="text-sm text-muted-foreground mt-1">Design your personal learning companion in 4 steps.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        {/* Left — wizard */}
        <div className="xl:col-span-2 space-y-6">
          {/* Step indicator */}
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-0">
              {steps.map((step, i) => {
                const Icon = step.icon;
                const isActive = currentStep === step.id;
                const isDone = currentStep > step.id;
                return (
                  <React.Fragment key={`step-indicator-${step.id}`}>
                    <button
                      onClick={() => isDone && setCurrentStep(step.id)}
                      className={`flex flex-col items-center gap-1.5 flex-1 group ${isDone ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                        isDone
                          ? 'bg-primary border-primary text-primary-foreground'
                          : isActive
                          ? 'bg-primary/10 border-primary text-primary' :'bg-muted border-border text-muted-foreground'
                      }`}>
                        {isDone ? <CheckCircle2 size={18} /> : <Icon size={18} />}
                      </div>
                      <div className="text-center hidden sm:block">
                        <p className={`text-xs font-semibold ${isActive ? 'text-primary' : isDone ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {step.label}
                        </p>
                        <p className="text-xs text-muted-foreground hidden md:block">{step.desc}</p>
                      </div>
                    </button>
                    {i < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mb-6 mx-1 rounded-full transition-all ${isDone || (isActive && i < currentStep - 1) ? 'bg-primary' : 'bg-muted'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Step content */}
          <div className="bg-card border border-border rounded-2xl p-6 fade-in">
            {currentStep === 1 && (
              <TutorBuilderStep1
                config={config}
                updateConfig={updateConfig}
                onNext={() => setCurrentStep(2)}
              />
            )}
            {currentStep === 2 && (
              <TutorBuilderStep2
                config={config}
                updateConfig={updateConfig}
                onNext={() => setCurrentStep(3)}
                onBack={() => setCurrentStep(1)}
              />
            )}
            {currentStep === 3 && (
              <TutorBuilderStep3
                config={config}
                updateConfig={updateConfig}
                onNext={() => setCurrentStep(4)}
                onBack={() => setCurrentStep(2)}
              />
            )}
            {currentStep === 4 && (
              <TutorBuilderStep4
                config={config}
                updateConfig={updateConfig}
                onBack={() => setCurrentStep(3)}
                onPreview={() => setShowPreview(true)}
                onSave={handleSave}
                saved={saved}
              />
            )}
          </div>
        </div>

        {/* Right — live preview */}
        <div className="space-y-4">
          <TutorBuilderPreview config={config} currentStep={currentStep} />
        </div>
      </div>

      {/* Preview modal */}
      {showPreview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/20 backdrop-blur-sm"
          onClick={() => setShowPreview(false)}
        >
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-modal fade-in" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-foreground mb-4">Tutor Preview — {config.name || 'Unnamed Tutor'}</h2>
            <div className="p-4 bg-muted/50 rounded-xl mb-4">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">{config.avatar}</span>
                <div>
                  <p className="font-bold text-foreground">{config.name || 'Your AI Tutor'}</p>
                  <p className="text-xs text-muted-foreground capitalize">{config.tone} · {config.personality}</p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-end">
                  <div className="bg-primary text-primary-foreground text-sm px-3 py-2 rounded-xl rounded-br-sm max-w-xs">
                    Can you explain what a variable is?
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className="bg-muted text-foreground text-sm px-3 py-2 rounded-xl rounded-bl-sm max-w-xs">
                    {config.personality === 'playful'
                      ? "Sure! 🎉 Think of a variable like a box with a label on it — you can put anything inside and change it whenever you want!"
                      : config.personality === 'formal'
                      ? "A variable is a named storage location in memory that holds a value which can be modified during program execution."
                      : "Great question! A variable is like a labeled jar — you give it a name, put a value inside, and can swap that value anytime. Want to try one?"}
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowPreview(false)}
              className="w-full py-2.5 bg-primary text-primary-foreground font-semibold rounded-xl btn-scale hover:bg-primary/90 transition-colors"
            >
              Close Preview
            </button>
          </div>
        </div>
      )}
    </div>
  );
}